import axios from "./axios";
import axiosBase from "axios";

const LOGIN_URL = "/oauth/token";

const CLIENT_ID = process.env.REACT_APP_CLIENT_ID;
const CLIENT_SECRET = process.env.REACT_APP_CLIENT_SECRET;

// Validate environment variables
if (!CLIENT_ID || !CLIENT_SECRET) {
  console.error("Missing CLIENT_ID or CLIENT_SECRET environment variables");
}

const TOKEN_STORAGE_KEY = "airon_access_token";
const TOKEN_EXPIRY_KEY = "airon_token_expiry";

// Token refresh queue to prevent multiple simultaneous refresh requests
let tokenRefreshPromise = null;

// Create a separate axios instance for token refresh to avoid interceptor loops
const tokenRefreshAxios = axiosBase.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * Calculate token expiration time (default 2 hours, but can be adjusted based on API response)
 */
function calculateTokenExpiry(expiresIn = 7200) {
  // expiresIn is in seconds, convert to milliseconds and subtract 5 minutes buffer
  return Date.now() + (expiresIn - 300) * 1000;
}

/**
 * Check if token is expired or about to expire
 */
export function isTokenExpired() {
  const expiryTime = localStorage.getItem(TOKEN_EXPIRY_KEY);
  if (!expiryTime) return true;
  return Date.now() >= parseInt(expiryTime, 10);
}

/**
 * Store token and expiration time
 */
function storeToken(token, expiresIn) {
  localStorage.setItem(TOKEN_STORAGE_KEY, token);
  localStorage.setItem(TOKEN_EXPIRY_KEY, calculateTokenExpiry(expiresIn).toString());
}

/**
 * Get stored token
 */
function getStoredToken() {
  return localStorage.getItem(TOKEN_STORAGE_KEY);
}

/**
 * Clear stored token
 */
function clearStoredToken() {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
}

/**
 * Fetch a new access token from the API
 * Uses a separate axios instance to avoid interceptor loops
 */
async function fetchNewToken() {
  if (!CLIENT_ID || !CLIENT_SECRET) {
    const error = new Error("CLIENT_ID or CLIENT_SECRET is missing");
    console.error("fetchNewToken error:", error);
    throw error;
  }

  const data = {
    grant_type: "client_credentials",
    scope: "public",
    client_id: CLIENT_ID,
    client_secret: CLIENT_SECRET,
  };

  try {
    // Use tokenRefreshAxios to avoid interceptor loops
    const response = await tokenRefreshAxios.post(LOGIN_URL, data);

    if (!response || !response.data) {
      throw new Error("Invalid response from token endpoint");
    }

    const tokenData = response.data;
    const accessToken = tokenData.access_token;
    const expiresIn = tokenData.expires_in || 7200; // Default 2 hours

    if (!accessToken) {
      throw new Error("No access token in response");
    }

    storeToken(accessToken, expiresIn);
    return accessToken;
  } catch (error) {
    console.error("fetchNewToken error:", error);
    if (error.response) {
      console.error("Response status:", error.response.status);
      console.error("Response data:", error.response.data);
    }
    clearStoredToken();
    throw error;
  }
}

/**
 * Get access token - checks if stored token is valid, otherwise fetches new one
 */
export async function getAccessToken(forceRefresh = false) {
  try {
    // If forcing refresh or token is expired, fetch new token
    if (forceRefresh || isTokenExpired()) {
      // If there's already a refresh in progress, wait for it
      if (tokenRefreshPromise) {
        return tokenRefreshPromise;
      }

      // Start new refresh
      tokenRefreshPromise = fetchNewToken()
        .then((token) => {
          tokenRefreshPromise = null;
          if (!token) {
            throw new Error("Token is null or undefined");
          }
          return token;
        })
        .catch((error) => {
          tokenRefreshPromise = null;
          console.error("Error fetching new token:", error);
          throw error;
        });

      return tokenRefreshPromise;
    }

    // Return stored token if still valid
    const storedToken = getStoredToken();
    if (storedToken) {
      return storedToken;
    }

    // Fallback: fetch new token if no stored token
    return getAccessToken(true);
  } catch (error) {
    console.error("getAccessToken error:", error);
    throw error;
  }
}

export async function getWebshopSettings() {
  return axios
    .get(`/company/settings/store`)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return error;
    });
}
